import { memo, Suspense, useContext, useEffect, useMemo, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import { StepSkeleton } from "../components/ui/step-skeleton";
import { amplitudeEnabled } from "../core/amplitude-provider";
import { useReadAnalyticsSessionId } from "../core/analytics-session";
import { useProduct } from "../hooks/use-product";
import { OnboardingV2RpcClientContext } from "../rpc-client";
import { STEP_COMPONENTS, STEP_PREFETCH } from "../step-components";
import { STEP_SKELETONS } from "../step-skeletons";

export const StepRenderer = memo(function StepRenderer() {
  const product = useProduct();
  const { stepPath } = useParams<{ stepPath: string }>();
  const rpcClient = useContext(OnboardingV2RpcClientContext);
  const savedRef = useRef<string | null>(null);
  const analyticsSavedRef = useRef<string | null>(null);
  const readAnalyticsSessionId = useReadAnalyticsSessionId();

  const steps = useMemo(() => product?.steps ?? [], [product?.steps]);
  const stepIndex = steps.findIndex((s) => s.path === stepPath);
  const step = stepIndex >= 0 ? steps[stepIndex] : undefined;

  // Fire saveProgress on step entry for bounce tracking (Decision #4)
  useEffect(() => {
    if (!product || !step || !rpcClient) return;
    const key = `${product.id}:${step.id}`;
    if (savedRef.current === key) return;
    savedRef.current = key;
    rpcClient
      .saveProgress({ productId: product.id, currentStepId: step.id })
      .catch(() => {});
  }, [product, step, rpcClient]);

  useEffect(() => {
    if (!product || !step || !rpcClient || !amplitudeEnabled) return;

    let attempts = 0;
    let intervalId: number | undefined;

    const tryBackfillAnalyticsSession = () => {
      const analyticsSessionId = readAnalyticsSessionId();
      if (!analyticsSessionId) {
        return false;
      }

      const key = `${product.id}:${step.id}:${analyticsSessionId}`;
      if (analyticsSavedRef.current === key) {
        return true;
      }

      analyticsSavedRef.current = key;
      rpcClient
        .saveProgress({
          productId: product.id,
          currentStepId: step.id,
          analyticsSessionId,
        })
        .catch(() => {});
      return true;
    };

    if (!tryBackfillAnalyticsSession()) {
      intervalId = window.setInterval(() => {
        attempts += 1;
        if (tryBackfillAnalyticsSession() || attempts >= 30) {
          window.clearInterval(intervalId);
        }
      }, 1000);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [product, step, rpcClient, readAnalyticsSessionId]);

  // Prefetch next step's component (Decision #15)
  useEffect(() => {
    if (stepIndex < 0 || stepIndex >= steps.length - 1) return;
    const nextStep = steps[stepIndex + 1];
    if (nextStep) {
      STEP_PREFETCH[nextStep.id]?.();
    }
  }, [steps, stepIndex]);

  const Component = step
    ? STEP_COMPONENTS[step.id]
    : STEP_COMPONENTS[stepPath!];
  if (!Component) {
    return <Navigate replace to={`/${product?.id ?? ""}`} />;
  }

  const stepId = step?.id ?? stepPath!;
  const skeletonConfig = STEP_SKELETONS[stepId];

  return (
    <Suspense fallback={<StepSkeleton config={skeletonConfig} />}>
      <Component />
    </Suspense>
  );
});

export default StepRenderer;
